import React, {useCallback, useRef, useState, createContext, useContext, useEffect} from 'react';
import {LinearInterpolator, FlyToInterpolator} from '@deck.gl/core';
import {fetchMap, setDefaultCredentials} from '@deck.gl/carto';

import {getTripsLayer} from '/layers/trips';
import slides from './slides';

const BASEMAP = './assets/darkmatter-high-contrast.json';
const initAppState = {
  currentSlide: null,
  viewState: {
    longitude: -0.15,
    latitude: 51.5,
    zoom: 11,
    bearing: 270,
    pitch: 50
  }
};

export const AppStateContext = createContext(initAppState);

const transitionInterpolator = new LinearInterpolator(['bearing', 'longitude', 'latitude']);

const DRIVERS_LABEL = 'Driver locations'
function getDriversLayer(layers) {
  return layers.filter(l => l.props.cartoLabel === DRIVERS_LABEL)[0];
}

export const AppStateStore = ({children}) => {
  const [currentSlide, setCurrentSlide] = useState(initAppState.currentSlide);
  const [layers, setLayers] = useState([]);
  const [viewState, setViewState] = useState(initAppState.viewState);

  const [animationFlag, setAnimationFlag] = React.useState();
  const forceUpdate = React.useCallback(() => setAnimationFlag({}), []);

  // Animation using the TripsLayer
  const maxTime = 50000;
  let currentTime = useRef(0);
  let requestRef = useRef(null);

  const orbit = useCallback(() => {
    setViewState((viewState) => ({
      ...viewState,
      bearing: viewState.bearing + 120,
      transitionDuration: 20000,
      transitionInterpolator,
      onTransitionEnd: orbit
    }));
  }, []);

  const updateViewState = function (viewState, shouldOrbit) {
    setViewState({
      ...viewState,
      transitionDuration: 2000,
      transitionInterpolator: new FlyToInterpolator(),
      onTransitionEnd: () => {
        if (shouldOrbit) {
          orbit();
        }
      }
    });
  };

  useEffect(async () => {
    setDefaultCredentials({
      accessToken:
        'eyJhbGciOiJIUzI1NiJ9.eyJhIjoiYWNfbHFlM3p3Z3UiLCJqdGkiOiI1YjI0OWE2ZCJ9.Y7zB30NJFzq5fPv8W5nkoH5lPXFWQP0uywDtqUg8y8c'
    });

    setCurrentSlide(0);
  }, [setCurrentSlide]);

  useEffect(() => {
    if (currentSlide !== null) {
      const {
        mapID: cartoMapId,
        layers,
        view,
        orbit: shouldOrbit,
        parameters,
        props,
        embed,
        image
      } = slides[currentSlide];
      if (embed === undefined && image === undefined) {
        if (cartoMapId) {
          fetchMap({cartoMapId}).then(
            ({initialViewState: mapViewState, mapStyle, layers: mapLayers}) => {
              let _mapLayers = mapLayers.map((l) =>
                l.clone({
                  parameters: {...l.props.parameters, ...parameters},
                  ...props
                })
              );

              if (currentSlide <= 1) {
                // For the first two slides (using the same map), we need to transform the
                // Driver Locations layer from CartoLayer to TripsLayer
                const label = 'Driver locations'
                const driversLayer = getDriversLayer(_mapLayers);
                if (!driversLayer) {
                  console.error(`Cannot find drivers layer. Check the map has a layer with label: "${DRIVERS_LABEL}"`);
                }
                _mapLayers = _mapLayers.map(l => {
                  return l === driversLayer ? getTripsLayer(l) : l
                });
              }

              setLayers(_mapLayers);
              if (currentSlide != 0) {
                // For the first slide, we don't load the map viewstate
                updateViewState(mapViewState, shouldOrbit);
              }
              if (currentSlide <= 1) {
                // Trigger the animation manually by re-rendering the component so the animation useEffect hook is executed
                forceUpdate();
              }
            }
          );
        } else {
          setLayers(layers.map((l) => l.clone()));
          if (view && view.longitude !== undefined) {
            updateViewState(view, shouldOrbit);
          }
        }
      }
    }
  }, [currentSlide]);

  // Animation
  const animate = () => {
    currentTime.current = (currentTime.current + 10) % maxTime;
    if (layers.length > 0 && currentSlide <= 1) {
      setLayers(layers.map(l => l.clone({currentTime: currentTime.current})));
      requestRef.current = requestAnimationFrame(animate);
    }
  }
  
  React.useEffect(() => {
    if (currentSlide <= 1) {
      requestRef.current = requestAnimationFrame(animate);
    }
    return () => cancelAnimationFrame(requestRef.current);
  }, [currentSlide, animationFlag]); 

  return (
    <AppStateContext.Provider
      value={{
        next: () => {
          if (currentSlide < slides.length - 1) {
            setCurrentSlide(currentSlide + 1);
          }
        },
        prev: () => {
          if (currentSlide > 0) {
            setCurrentSlide(currentSlide - 1);
          }
        },
        reset: () => {
          setCurrentSlide(0);
        },
        currentSlide,
        layers,
        slidesNumber: slides.length,
        viewState,
        basemap: BASEMAP
      }}
    >
      {children}
    </AppStateContext.Provider>
  );
};

export const AppStateContextConsumer = AppStateContext.Consumer;

export function useAppState() {
  return useContext(AppStateContext);
}
